import { Inject, Injectable, Optional } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { UserModel } from '@app/models/server/user.model';
import { map, switchMap, tap } from 'rxjs/operators';
import { STORAGE_CONST } from '@app/constants/storage-constants';
import {
  LocalStorageUtilLibService,
  StorageUtilLibService,
} from '@b2b/storage-util-lib';
import { environment } from '@src/environments/environment';
import { Router } from '@angular/router';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/store';
import * as initUser from '@app/store/init-user';
import { HttpHeaders } from '@angular/common/http';
import { HttpClientService } from '@app/services/util-services/root-services/http-client.service';
import { DOCUMENT } from '@angular/common';
import { decodeJwt, JWTPayload } from 'jose';

@Injectable({ providedIn: 'root' })
export class InitAuthService {
  userInfo: UserModel = new UserModel();
  authToken: string;
  firebaseToken: string;
  refreshToken: string;

  constructor(
    private storageUtilLibService: StorageUtilLibService,
    private localStorageUtilLibService: LocalStorageUtilLibService,
    private router: Router,
    @Optional() @Inject('WINDOW') private window: any,
    private store: Store<fromRoot.State>,
    private eventService: EventService,
    private httpClientService: HttpClientService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.eventService.loggedOutEvent.subscribe({
      next: (_res) => {
        this.logout();
      },
    });
  }

  public getAuthToken(): Observable<string> {
    if (this.authToken) {
      return of(this.authToken).pipe(
        tap((authToken: string) => {
          this.setB2BToken(authToken).then();
        }),
      );
    } else {
      return this.storageUtilLibService.getToken(STORAGE_CONST.AUTH_TOKEN).pipe(
        switchMap((token: string) => {
          if (!token) {
            this.authToken = undefined;
            this.firebaseToken = undefined;
            this.removeB2BToken().then();
            this.removeFirebaseToken().then();
          }
          return of(token);
        }),
        tap((token: string) => {
          this.authToken = token;
          this.setB2BToken(token).then();
        }),
      );
    }
  }

  setB2BToken(token: string): Promise<any> {
    if (token) {
      this.authToken = token;
      if (environment.webApp) {
        this.localStorageUtilLibService.set(STORAGE_CONST.AUTH_TOKEN, token);
      }
      return this.storageUtilLibService.setToken(
        STORAGE_CONST.AUTH_TOKEN,
        token,
      );
    } else {
      return of(null).toPromise();
    }
  }

  public getUserFromToken(token: string): UserModel {
    try {
      const jwtTokenData: JWTPayload = decodeJwt(token);
      if (!jwtTokenData?.user) {
        this.removeB2BToken().then();
        return null;
      }
      return jwtTokenData?.user as UserModel;
    } catch (e) {
      console.error('Handling invalid token e ', e);
    }
    return null;
  }

  removeB2BToken(): Promise<any> {
    this.authToken = undefined;
    if (environment.webApp) {
      this.localStorageUtilLibService.remove(STORAGE_CONST.AUTH_TOKEN);
    }
    return this.storageUtilLibService.removeToken(STORAGE_CONST.AUTH_TOKEN);
  }

  createRefreshToken(
    refreshToken: string,
    authToken: string,
  ): Observable<{
    token: string;
    refreshToken: string;
  }> {
    const options = {
      headers: new HttpHeaders()
        .set('x-refresh-token', `${refreshToken}`)
        .append('authorization', authToken),
    };

    return this.httpClientService
      .post('auth-service/refresh-token', {}, options)
      .pipe(
        map(
          (result: {
            token: string;
            refreshToken: string;
            accessToken: string;
          }) => {
            this.authToken = result?.token;
            this.refreshToken = result?.refreshToken;
            this.setRefreshToken(result?.refreshToken);
            this.setB2BToken(result?.token).then();
            return result;
          },
        ),
      );
  }

  public logout() {
    this.store.dispatch(initUser.SignOut({}));
    from(this.removeFirebaseToken())
      .pipe(
        switchMap((item: any) => {
          return from(this.removeB2BToken());
        }),
      )
      .subscribe({
        next: (result: any) => {
          this.authToken = undefined;
          this.firebaseToken = undefined;
          this.handleRedirectAfterLogout();
        },
        error: (error: any) => {
          // console.warn('Error while logging out the user.', error);
        },
      });
  }

  handleRedirectAfterLogout() {
    if (environment.webApp) {
      const url: string = this.router.url;

      if (url.indexOf('auth') > -1) {
        this.router.navigateByUrl('/').then((r) => {});
      } else if (url.indexOf('user/') > -1) {
        this.router.navigateByUrl('/').then((r) => {});
      } else if (url.indexOf('feature/checkout') > -1) {
        this.router.navigateByUrl('/').then((r) => {});
      } else {
        if (this.window) {
          this.window.location.reload();
        } else {
          this.router.navigateByUrl('/').then((r) => {});
        }
      }
    } else if (environment.mobileApp) {
      const url: string = this.router.url;

      if (url.indexOf('user/') > -1) {
        this.router.navigateByUrl('/').then((r) => {});
      } else {
        if (this.document?.defaultView) {
          this.document?.defaultView?.location?.reload();
        } else {
          this.router.navigateByUrl('/').then((r) => {});
        }
      }
    }
  }

  afterDeleteAccount() {
    this.logout();
  }

  public isAuthenticated(): Observable<boolean> {
    if (this.authToken) {
      return of(!this.isTokenExpired(this.authToken));
    }
    return this.getAuthToken().pipe(
      switchMap((token: string) => {
        return of(!this.isTokenExpired(token));
      }),
    );
  }

  isTokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }
    try {
      const jwtTokenData: JWTPayload = decodeJwt(token);
      if (jwtTokenData?.exp) {
        return Date.now() >= jwtTokenData?.exp * 1000;
      }
    } catch (e) {
      console.error('Handling invalid token e ', e);
    }

    return true;
  }

  setFirebaseToken(token: string): void {
    if (token) {
      this.firebaseToken = token;
      this.storageUtilLibService
        .setToken(STORAGE_CONST.FIREBASE_AUTH_TOKEN, token)
        .then();

      if (environment.webApp) {
        this.localStorageUtilLibService.set(
          STORAGE_CONST.FIREBASE_AUTH_TOKEN,
          token,
        );
      }
    }
  }

  removeFirebaseToken(): Promise<any> {
    this.firebaseToken = undefined;
    if (environment.webApp) {
      this.localStorageUtilLibService.remove(STORAGE_CONST.FIREBASE_AUTH_TOKEN);
    }
    return this.storageUtilLibService.removeToken(
      STORAGE_CONST.FIREBASE_AUTH_TOKEN,
    );
  }

  setRefreshToken(token: string): void {
    if (token) {
      this.refreshToken = token;
      this.storageUtilLibService
        .setToken(STORAGE_CONST.REFRESH_TOKEN, token)
        .then();

      if (environment.webApp) {
        this.localStorageUtilLibService.set(STORAGE_CONST.REFRESH_TOKEN, token);
      }
    }
  }

  removeRefreshToken(): Promise<any> {
    this.refreshToken = undefined;
    if (environment.webApp) {
      this.localStorageUtilLibService.remove(STORAGE_CONST.REFRESH_TOKEN);
    }
    return this.storageUtilLibService.removeToken(STORAGE_CONST.REFRESH_TOKEN);
  }
}
